import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import JsonLd from "@/components/feature/JsonLd";
import {
  buildArticleSchema,
  buildBreadcrumbSchema,
  buildFaqSchema,
  buildOrganizationSchema,
  toLanguageTag,
} from "@/seo/schema";
import type { Article, ArticleContent } from "@/mocks/articles";
import { articles } from "@/mocks/articles";
import { MAIN_SITE_URL } from "@/mocks/categories";

interface ArticleLayoutProps {
  article: Article;
}

const LANG_KEYS = ["en", "pl", "es", "fr"] as const;

export default function ArticleLayout({ article }: ArticleLayoutProps) {
  const { t, i18n } = useTranslation();

  const lang = (LANG_KEYS.includes(i18n.language as (typeof LANG_KEYS)[number])
    ? i18n.language
    : "en") as (typeof LANG_KEYS)[number];

  const content: ArticleContent = article[lang];

  const dateStr = new Date(article.updated).toLocaleDateString(lang, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  const relatedArticles = article.related
    .map((slug) => articles.find((a) => a.slug === slug))
    .filter((a): a is Article => Boolean(a));

  return (
    <>
      <JsonLd
        data={[
          buildOrganizationSchema(),
          buildArticleSchema({
            headline: content.title,
            description: content.metaDescription,
            path: `/article/${article.slug}`,
            dateModified: article.updated,
            language: toLanguageTag(lang),
            category: content.categoryLabel,
          }),
          buildBreadcrumbSchema([
            { name: t("nav.home"), path: "/" },
            { name: content.categoryLabel, path: `/article/${article.slug}` },
          ]),
          buildFaqSchema(content.faq),
        ]}
      />
      <main className="bg-background-50">
      <div className="mx-auto w-full max-w-3xl px-4 py-10 md:px-6 md:py-14">
        {/* Breadcrumb */}
        <nav aria-label="Breadcrumb" className="flex items-center gap-1.5 text-sm text-foreground-500">
          <Link to="/" className="transition-colors hover:text-primary-700">
            {t("nav.home")}
          </Link>
          <span className="flex h-4 w-4 items-center justify-center">
            <i className="ri-arrow-right-s-line"></i>
          </span>
          <span className="text-foreground-800">{content.categoryLabel}</span>
        </nav>

        {/* Header */}
        <header className="mt-6">
          <span className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-secondary-100 px-3 py-1 text-xs font-semibold text-secondary-900">
            <span className="flex h-3 w-3 items-center justify-center">
              <i className="ri-book-open-line"></i>
            </span>
            {content.categoryLabel}
          </span>
          <h1 className="mt-4 font-heading text-3xl font-bold leading-tight tracking-tight text-foreground-950 md:text-4xl">
            {content.title}
          </h1>
          <div className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-foreground-500">
            <span className="flex items-center gap-1.5">
              <span className="flex h-4 w-4 items-center justify-center text-foreground-400">
                <i className="ri-user-line"></i>
              </span>
              {t("article.author")}
            </span>
            <span className="flex items-center gap-1.5">
              <span className="flex h-4 w-4 items-center justify-center text-foreground-400">
                <i className="ri-calendar-line"></i>
              </span>
              {t("article.updated")}: {dateStr}
            </span>
            <span className="flex items-center gap-1.5">
              <span className="flex h-4 w-4 items-center justify-center text-foreground-400">
                <i className="ri-time-line"></i>
              </span>
              {content.readTime}
            </span>
          </div>
        </header>

        {/* Intro */}
        <div className="mt-8">
          <p className="text-base leading-relaxed text-foreground-700">{content.intro}</p>
        </div>

        {/* TOC */}
        <nav aria-label={t("article.tocTitle")} className="mt-8 rounded-lg border border-background-200 bg-background-100 p-5">
          <h2 className="font-heading text-sm font-semibold uppercase tracking-wider text-foreground-900">
            {t("article.tocTitle")}
          </h2>
          <ol className="mt-3 space-y-2">
            {content.sections.map((s, i) => (
              <li key={i}>
                <a
                  href={`#s${i}`}
                  className="flex items-start gap-2 text-sm text-foreground-600 transition-colors hover:text-primary-700"
                >
                  <span className="font-semibold text-primary-600">{i + 1}.</span>
                  {s.heading}
                </a>
              </li>
            ))}
            <li>
              <a
                href="#faq"
                className="flex items-start gap-2 text-sm text-foreground-600 transition-colors hover:text-primary-700"
              >
                <span className="font-semibold text-primary-600">{content.sections.length + 1}.</span>
                {t("article.faqTitle")}
              </a>
            </li>
          </ol>
        </nav>

        {/* Sections */}
        <article className="mt-10 space-y-12">
          {content.sections.map((s, i) => (
            <section key={i} id={`s${i}`} className="scroll-mt-24">
              <h2 className="font-heading text-2xl font-bold text-foreground-950">{s.heading}</h2>
              <p className="mt-4 text-base leading-relaxed text-foreground-700">{s.body}</p>
            </section>
          ))}

          {/* FAQ */}
          <section id="faq" className="scroll-mt-24">
            <h2 className="font-heading text-2xl font-bold text-foreground-950">{t("article.faqTitle")}</h2>
            <div className="mt-5 space-y-3">
              {content.faq.map((item, i) => (
                <div key={i} className="rounded-lg border border-background-200 bg-background-50 p-5">
                  <h3 className="font-heading text-sm font-semibold text-foreground-900 md:text-base">
                    {item.q}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.a}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Related articles */}
          <section className="rounded-lg border border-background-200 bg-background-100 p-6">
            <h2 className="font-heading text-lg font-semibold text-foreground-950">
              {t("article.relatedTitle")}
            </h2>
            <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
              {relatedArticles.map((r) => {
                const rc = r[lang];
                return (
                  <Link
                    key={r.slug}
                    to={`/article/${r.slug}`}
                    className="flex items-center gap-2 rounded-md border border-background-200 bg-background-50 px-4 py-3 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-700"
                  >
                    <span className="flex h-4 w-4 shrink-0 items-center justify-center text-primary-600">
                      <i className="ri-article-line"></i>
                    </span>
                    {rc.title}
                  </Link>
                );
              })}
            </div>
            <Link
              to="/"
              className="mt-5 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
            >
              {t("article.backHome")}
              <span className="flex h-4 w-4 items-center justify-center">
                <i className="ri-arrow-left-line"></i>
              </span>
            </Link>
          </section>

          {/* CTA */}
          <section className="rounded-lg bg-primary-500 px-6 py-10 text-center">
            <h2 className="font-heading text-xl font-bold text-background-50 md:text-2xl">
              {t("article.ctaTitle")}
            </h2>
            <p className="mx-auto mt-3 max-w-lg text-sm leading-relaxed text-background-100">
              {t("article.ctaBody")}
            </p>
            <a
              href={MAIN_SITE_URL}
              target="_blank"
              rel="noopener nofollow"
              className="mt-6 inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-background-50 px-6 py-3 text-sm font-semibold text-primary-700 transition-colors hover:bg-background-100"
            >
              {t("article.ctaButton")}
              <span className="flex h-4 w-4 items-center justify-center">
                <i className="ri-arrow-right-up-line"></i>
              </span>
            </a>
          </section>
        </article>
      </div>
      </main>
    </>
  );
}